// src/hooks/useSearch.ts
import { useEffect, useMemo, useRef, useState } from 'react';
import { searchContent as searchApi } from '@/lib/firebase';
import type { SearchResult } from '@/lib/firebase/search';
import { useAuth } from '@/providers/AuthProvider';

type SearchType = SearchResult['type'] | 'all';

interface SearchIntent {
  query: string;
  type: SearchType;
}

interface RecentSearchEntry {
  query: string;
  type: SearchType;
  timestamp: number;
  resultCount: number;
}

interface SearchSignals {
  typeClicks: Record<string, number>;
  resultClicks: Record<string, number>;
}

const RECENT_SEARCHES_KEY = 'intouch.search.recent.v1';
const SEARCH_SIGNALS_KEY = 'intouch.search.signals.v1';

const VALID_SEARCH_TYPES: SearchType[] = [
  'all',
  'users',
  'posts',
  'groups',
  'events',
  'marketplace',
  'music',
  'hotels',
  'apartments',
  'pages',
  'restaurants',
  'recipes'
];

function readStorageValue<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeStorageValue<T>(key: string, value: T) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore quota or storage access errors to keep search usable.
  }
}

function parseSearchIntent(searchQuery: string, fallbackType: SearchType): SearchIntent {
  const trimmed = searchQuery.trim();
  if (!trimmed) {
    return { query: '', type: fallbackType };
  }

  const tokenMatches = Array.from(trimmed.matchAll(/(?:^|\s)(?:type|in):([a-z]+)/gi));
  const forcedTypeRaw = tokenMatches.at(-1)?.[1]?.toLowerCase() as SearchType | undefined;
  const forcedType = forcedTypeRaw && VALID_SEARCH_TYPES.includes(forcedTypeRaw) ? forcedTypeRaw : undefined;

  const cleanedQuery = trimmed
    .replace(/(?:^|\s)(?:type|in):[a-z]+/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return {
    query: cleanedQuery,
    type: forcedType || fallbackType,
  };
}

function scorePersonalization(result: SearchResult, signals: SearchSignals): number {
  const resultKey = `${result.type}:${result.id}`;
  const typeClicks = signals.typeClicks[result.type] || 0;
  const resultClicks = signals.resultClicks[resultKey] || 0;

  const resultBoost = Math.min(resultClicks * 9, 36);
  const typeBoost = Math.min(typeClicks * 4, 20);
  return resultBoost + typeBoost;
}

function diversifyAllResults(results: SearchResult[]): SearchResult[] {
  const buckets = new Map<string, SearchResult[]>();

  for (const result of results) {
    const bucket = buckets.get(result.type) || [];
    bucket.push(result);
    buckets.set(result.type, bucket);
  }

  const types = Array.from(buckets.keys());
  if (types.length <= 1) return results;

  const diversified: SearchResult[] = [];

  let keepLooping = true;
  while (keepLooping) {
    keepLooping = false;
    for (const type of types) {
      const bucket = buckets.get(type);
      const next = bucket?.shift();
      if (next) {
        diversified.push(next);
        keepLooping = true;
      }
    }
  }

  return diversified;
}

export function useSearch() {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [effectiveType, setEffectiveType] = useState<SearchType>('all');
  const [parsedQuery, setParsedQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState<RecentSearchEntry[]>([]);
  const { user } = useAuth();
  const requestIdRef = useRef(0);
  const cacheRef = useRef<Map<string, SearchResult[]>>(new Map());
  const signalsRef = useRef<SearchSignals>({ typeClicks: {}, resultClicks: {} });

  useEffect(() => {
    setRecentSearches(readStorageValue<RecentSearchEntry[]>(RECENT_SEARCHES_KEY, []));
    signalsRef.current = readStorageValue<SearchSignals>(SEARCH_SIGNALS_KEY, { typeClicks: {}, resultClicks: {} });
  }, []);

  const topCategories = useMemo(() => {
    const categoryCounts = results.reduce<Record<string, number>>((acc, item) => {
      const key = item.category || item.type;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(categoryCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([category, count]) => ({ category, count }));
  }, [results]);

  const persistRecentSearch = (query: string, type: SearchType, resultCount: number) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized || normalized.length < 2) return;

    const next = [
      {
        query: normalized,
        type,
        timestamp: Date.now(),
        resultCount,
      },
      ...recentSearches.filter((entry) => !(entry.query === normalized && entry.type === type)),
    ].slice(0, 10);

    setRecentSearches(next);
    writeStorageValue(RECENT_SEARCHES_KEY, next);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    writeStorageValue(RECENT_SEARCHES_KEY, []);
  };

  const trackResultInteraction = (result: SearchResult) => {
    const resultKey = `${result.type}:${result.id}`;
    const nextSignals: SearchSignals = {
      typeClicks: {
        ...signalsRef.current.typeClicks,
        [result.type]: (signalsRef.current.typeClicks[result.type] || 0) + 1,
      },
      resultClicks: {
        ...signalsRef.current.resultClicks,
        [resultKey]: (signalsRef.current.resultClicks[resultKey] || 0) + 1,
      },
    };

    signalsRef.current = nextSignals;
    writeStorageValue(SEARCH_SIGNALS_KEY, nextSignals);
  };

  const searchContent = async (searchQuery: string, type: SearchType = 'all') => {
    const intent = parseSearchIntent(searchQuery, type);
    const normalizedQuery = intent.query.trim().toLowerCase();
    setEffectiveType(intent.type);
    setParsedQuery(normalizedQuery);

    if (!normalizedQuery || !user) return;

    const cacheKey = `${intent.type}:${normalizedQuery}`;
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setResults(cached);
      return;
    }

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    setLoading(true);
    try {
      const searchResults = await searchApi(normalizedQuery, intent.type);
      if (requestId !== requestIdRef.current) {
        return;
      }

      const ranked = [...searchResults]
        .map((item) => {
          const personalizationScore = scorePersonalization(item, signalsRef.current);
          return {
            ...item,
            relevanceScore: (item.relevanceScore || 0) + personalizationScore,
          };
        })
        .sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0));

      const finalResults = intent.type === 'all' ? diversifyAllResults(ranked) : ranked;

      setResults(finalResults);
      cacheRef.current.set(cacheKey, finalResults);
      persistRecentSearch(normalizedQuery, intent.type, finalResults.length);
      if (cacheRef.current.size > 30) {
        const oldestKey = cacheRef.current.keys().next().value;
        if (oldestKey) {
          cacheRef.current.delete(oldestKey);
        }
      }
    } catch (error) {
      if (requestId === requestIdRef.current) {
        console.error('Error searching content:', error);
        setResults([]);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  return {
    results,
    loading,
    effectiveType,
    parsedQuery,
    recentSearches,
    topCategories,
    clearRecentSearches,
    trackResultInteraction,
    searchContent,
  };
}
